import type { ThemeBreakpointScreenSize } from "@/services/theme";

import { useEffect } from "react";

import { TAB_KEYS, TAB_PARAM_KEYS } from "@/constants/TabKeys";
import { useSaveBar } from "@/modules/app-save-bar";
import {
  useDatasetsMutatorActions,
  useDatasetsMutatorStatus,
} from "@/modules/datasets/datasets-mutator";
import { useInterventionSectionRenderIndex } from "@/modules/intervention/intervention-section";
import { useIsTabActive } from "@/modules/tabs";
import { useThemeBreakpoints } from "@/services/theme";

export const useInterventionSectionSaveBarWrapper = (
  screenSize: ThemeBreakpointScreenSize
) => {
  const { screenSize: currentScreenSize } = useThemeBreakpoints();

  const isDetailsTabActive = useIsTabActive({
    tabKey: TAB_PARAM_KEYS.CONTEXT_TAB_KEY,
    tabValue: TAB_KEYS.INTERVENTIONS_DETAILS,
    isDefaultTab: true,
  });

  const showSaveBar = screenSize === currentScreenSize && isDetailsTabActive;

  return {
    showSaveBar,
    currentScreenSize,
  };
};

/**
 * Manages actions and state of the save bar in an intervention section
 *
 * - Handles save and cancel actions from save bar
 * - Synchronizes datasets mutator state with save bar state
 * - Forces remounting of dataset components on cancel click
 */
export const useInterventionSectionSaveBar = () => {
  const { setIsDirty, setIsSaving } = useSaveBar();

  const { isDirty, isSaving } = useDatasetsMutatorStatus();
  const { saveMutations, cancelMutations } = useDatasetsMutatorActions();
  const { incrementIndex } = useInterventionSectionRenderIndex();

  const onSaveClick = () => {
    saveMutations();
  };

  const onCancelClick = () => {
    cancelMutations();

    // Increment intervention section `renderIndex` on each click
    // to make sure datasets are re-mounted on cancel click.
    incrementIndex();
  };

  // Sync `datasets-mutator` and `app-save-bar` states
  useEffect(() => setIsDirty(isDirty), [isDirty]);
  useEffect(() => setIsSaving(isSaving), [isSaving]);

  return {
    onSaveClick,
    onCancelClick,
  };
};
